import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDays,
  cnInstant,
  fmtMonthDay,
  nextWorkday,
  type AnomalyDTO,
  type AssignmentDTO,
  type ExitRequestDTO,
  type ResolutionKind,
} from '@wd/shared';
import { errorText } from '../../lib/api';
import { serverNow, todayCN } from '../../lib/clock';
import { useHotkeys } from '../../lib/hotkeys';
import { fetchAskDraft, fetchAssignCandidates, useInboxActions, useMembers, type AssignCandidate } from '../../lib/queries';
import { Icon, StatusIcon } from '../../ui/icons';
import { AIAvatar, Avatar, Button, Checkbox, Kbds, Modal, Radio, Skeleton, Toggle } from '../../ui/primitives';

const CAL = { holidays: [] as string[], extraWorkdays: [] as string[] };

function useSubmitKey(fn: () => void, enabled = true) {
  useHotkeys({ 'modal:mod+Enter': fn }, enabled);
}

function ErrorLine({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div style={{ padding: '10px 20px 0', fontSize: 12, color: 'var(--status-blocked)' }} role="alert">
      {error}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 追问负责人（设计稿 13a）
// ---------------------------------------------------------------------------

export function AskModal({ anomaly, initialText, onClose }: { anomaly: AnomalyDTO; initialText?: string; onClose: () => void }) {
  const { ask } = useInboxActions();
  const { data: members = [] } = useMembers();
  const [toId, setToId] = useState(anomaly.ownerId);
  const [ccIds, setCcIds] = useState<string[]>([]);
  const [text, setText] = useState(initialText ?? '');
  const [alts, setAlts] = useState<string[]>([]);
  const [altIdx, setAltIdx] = useState(0);
  const [loading, setLoading] = useState(!initialText);
  const [requeue, setRequeue] = useState(true);
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (initialText) return;
    let alive = true;
    fetchAskDraft(anomaly.id)
      .then((d) => {
        if (!alive) return;
        setText(d.question);
        setAlts([d.question, ...d.alternatives]);
        setToId(d.toId);
      })
      .catch(() => alive && setText(`${anomaly.ownerName}，`))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [anomaly.id, anomaly.ownerName, initialText]);

  useEffect(() => {
    if (!loading) taRef.current?.focus();
  }, [loading]);

  const to = members.find((m) => m.id === toId);
  const send = async () => {
    if (!text.trim() || ask.isPending) return;
    try {
      await ask.mutateAsync({ id: anomaly.id, toId, ccIds, question: text, requeue });
      onClose();
    } catch (e) {
      setError(errorText(e));
    }
  };
  useSubmitKey(() => void send());

  return (
    <Modal
      width={580}
      title="追问负责人"
      onClose={onClose}
      footNote={`${to?.name ?? '对方'}会在微信收到，回复会出现在这条异常下`}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button variant="primary" disabled={!text.trim() || ask.isPending} onClick={() => void send()}>
            发送追问
          </Button>
          <Kbds keys={['⌘', '↵']} />
        </>
      }
    >
      <div style={{ margin: '-8px -20px 0' }}>
        <div className="ib-field">
          <span className="k">发给</span>
          <span className="v">
            <select className="ib-select" value={toId} onChange={(e) => setToId(e.target.value)} aria-label="发给">
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </span>
        </div>
        <div className="ib-field">
          <span className="k">抄送</span>
          <span className="v">
            {ccIds.map((id) => {
              const m = members.find((x) => x.id === id);
              return (
                <button key={id} type="button" className="ib-chip" title="移除" onClick={() => setCcIds(ccIds.filter((x) => x !== id))}>
                  <Avatar initial={m?.initial ?? '?'} seed={m?.name} />
                  {m?.name}
                </button>
              );
            })}
            {picking ? (
              <select
                className="ib-select"
                autoFocus
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) setCcIds([...ccIds, e.target.value]);
                  setPicking(false);
                }}
                onBlur={() => setPicking(false)}
                aria-label="添加抄送"
              >
                <option value="">选择成员…</option>
                {members
                  .filter((m) => m.id !== toId && !ccIds.includes(m.id))
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
              </select>
            ) : (
              <button type="button" className="c4" style={{ fontSize: 13 }} onClick={() => setPicking(true)}>
                + 添加
              </button>
            )}
          </span>
        </div>
        <div className="ib-field" style={{ marginBottom: 8 }}>
          <span className="k">关于</span>
          <span className="v">
            <StatusIcon status="blocked" />
            <span className="ellipsis">{anomaly.title}</span>
            <span className="c4">{anomaly.projectCode}</span>
          </span>
        </div>
        {loading ? (
          <div style={{ margin: '0 20px', minHeight: 96 }}>
            <Skeleton h={14} w="80%" />
            <Skeleton h={14} w="55%" style={{ marginTop: 10 }} />
          </div>
        ) : (
          <textarea
            ref={taRef}
            className="wd-textarea ib-text"
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            aria-label="追问内容"
          />
        )}
        <div className="row t-meta" style={{ gap: 8, padding: '10px 20px 0' }}>
          <AIAvatar size={14} />
          <span>AI 根据证据起草，可直接修改</span>
          <span className="flex1" />
          {alts.length > 1 && (
            <button
              type="button"
              className="c3"
              onClick={() => {
                const n = (altIdx + 1) % alts.length;
                setAltIdx(n);
                setText(alts[n]);
              }}
            >
              换一种问法
            </button>
          )}
        </div>
        <div className="row" style={{ gap: 10, padding: '16px 20px', fontSize: 13, color: 'var(--text-2)' }}>
          <Toggle on={requeue} onChange={setRequeue} label="未回复时放回收件箱" />
          <span>24 小时内未回复时，重新放回我的收件箱</span>
        </div>
        <ErrorLine error={error} />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 指派（设计稿 18a）
// ---------------------------------------------------------------------------

function dueOptions() {
  const today = todayCN();
  const now = serverNow();
  const opts: { label: string; iso: string }[] = [];
  const todayAt = (t: string) => cnInstant(today, t);
  if (todayAt('18:00') > now) opts.push({ label: '今天 18:00', iso: todayAt('18:00').toISOString() });
  const next = nextWorkday(today, CAL);
  opts.push({ label: `${next === addDays(today, 1) ? '明天' : fmtMonthDay(next)} 12:00`, iso: cnInstant(next, '12:00').toISOString() });
  opts.push({ label: `${next === addDays(today, 1) ? '明天' : fmtMonthDay(next)} 18:00`, iso: cnInstant(next, '18:00').toISOString() });
  const after = nextWorkday(next, CAL);
  opts.push({ label: `${fmtMonthDay(after)} 18:00`, iso: cnInstant(after, '18:00').toISOString() });
  const week = nextWorkday(addDays(today, 4), CAL);
  opts.push({ label: `${fmtMonthDay(week)} 18:00`, iso: cnInstant(week, '18:00').toISOString() });
  return opts;
}

export function AssignModal({
  anomaly,
  initialAssigneeName,
  initialAsk,
  onClose,
}: {
  anomaly: AnomalyDTO;
  initialAssigneeName?: string;
  initialAsk?: string;
  onClose: () => void;
}) {
  const { assign } = useInboxActions();
  const [cands, setCands] = useState<AssignCandidate[] | null>(null);
  const [q, setQ] = useState('');
  const [pick, setPick] = useState<string | null>(null);
  const [ask, setAsk] = useState(initialAsk ?? '');
  const opts = useMemo(dueOptions, []);
  const [due, setDue] = useState(opts[0].iso);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAssignCandidates(anomaly.id)
      .then((list) => {
        setCands(list);
        const byName = initialAssigneeName ? list.find((c) => c.name === initialAssigneeName) : undefined;
        setPick((byName ?? list.find((c) => c.recommended) ?? list[0])?.id ?? null);
      })
      .catch((e) => setError(errorText(e)));
  }, [anomaly.id, initialAssigneeName]);

  const shown = (cands ?? []).filter((c) => !q.trim() || `${c.name}${c.teamName ?? ''}`.includes(q.trim())).slice(0, 6);
  const send = async () => {
    if (!pick || !ask.trim() || assign.isPending) return;
    try {
      await assign.mutateAsync({ id: anomaly.id, assigneeId: pick, ask, dueAt: due });
      onClose();
    } catch (e) {
      setError(errorText(e));
    }
  };
  useSubmitKey(() => void send());

  return (
    <Modal
      width={560}
      title="指派给…"
      subtitle={anomaly.title}
      onClose={onClose}
      footNote="指派后你仍能看到进展；对方处理完需要给出结论"
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button variant="primary" disabled={!pick || !ask.trim() || assign.isPending} onClick={() => void send()}>
            指派
          </Button>
          <Kbds keys={['⌘', '↵']} />
        </>
      }
    >
      <div style={{ margin: '-16px -20px 0' }}>
        <div className="ib-search">
          <Icon name="search" size={14} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜索成员" aria-label="搜索成员" />
        </div>
        <div style={{ padding: '6px 0' }}>
          {!cands && (
            <div style={{ padding: '8px 20px' }}>
              <Skeleton h={14} />
            </div>
          )}
          {shown.map((c) => (
            <button key={c.id} type="button" className={clsx('ib-pick', pick === c.id && 'on')} onClick={() => setPick(c.id)}>
              <Avatar initial={c.initial} seed={c.name} size={20} />
              <span className="c1">{c.name}</span>
              <span className="t-meta">{[c.teamName, c.note === '负责人' ? '负责人' : null].filter(Boolean).join(' · ')}</span>
              {c.recommended && c.note && (
                <span className="row c3" style={{ gap: 4, fontSize: 12 }}>
                  <AIAvatar size={12} />
                  {c.note}
                </span>
              )}
              <span className="flex1" />
              <span className="t-meta">{c.load} 个项目</span>
              {pick === c.id && <Icon name="check" size={14} color="var(--text-2)" />}
            </button>
          ))}
        </div>
        <div className="t-group" style={{ padding: '8px 20px 6px' }}>
          请对方做什么
        </div>
        <textarea
          className="wd-textarea ib-text"
          rows={2}
          value={ask}
          placeholder="写清楚要对方做的事，例如：今天确认德语手册的翻译渠道和交付日期"
          onChange={(e) => setAsk(e.target.value)}
          aria-label="请对方做什么"
        />
        <div className="row" style={{ gap: 12, padding: '14px 20px', fontSize: 13 }}>
          <span className="c4" style={{ width: 44 }}>
            期限
          </span>
          <select className="ib-select" value={due} onChange={(e) => setDue(e.target.value)} aria-label="期限">
            {opts.map((o) => (
              <option key={o.iso} value={o.iso}>
                {o.label}
              </option>
            ))}
          </select>
          <span className="t-meta">到期未处理会回到你的收件箱</span>
        </div>
        <ErrorLine error={error} />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 标记已处理（设计稿 13c）
// ---------------------------------------------------------------------------

export function ResolveModal({ anomaly, onClose }: { anomaly: AnomalyDTO; onClose: () => void }) {
  const { resolve } = useInboxActions();
  const replied = anomaly.followUps.some((f) => f.answer);
  const lastAnswer = [...anomaly.followUps].reverse().find((f) => f.answer);
  const [kind, setKind] = useState<ResolutionKind>(replied ? 'following' : 'resolved');
  const [note, setNote] = useState('');
  const recheckDefault = useMemo(() => {
    let d = todayCN();
    for (let i = 0; i < 3; i++) d = nextWorkday(d, CAL);
    return d;
  }, []);
  const [recheck, setRecheck] = useState(recheckDefault);
  const [requeue, setRequeue] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const options: { kind: ResolutionKind; title: string; sub: string }[] = [
    { kind: 'resolved', title: '已解决', sub: anomaly.type === 'blocked_timeout' ? '阻塞已经解除' : '问题已经处理' },
    { kind: 'following', title: '有人跟进，等待结果', sub: lastAnswer?.answer ? `${lastAnswer.toName}：${lastAnswer.answer}` : '设一个复查日，到期仍未解除会放回收件箱' },
    { kind: 'false_positive', title: '不需要处理', sub: '这条判断不准确，AI 会减少类似提醒' },
  ];

  const send = async () => {
    if (resolve.isPending) return;
    try {
      await resolve.mutateAsync({
        id: anomaly.id,
        kind,
        note: note.trim() || null,
        recheckAt: kind === 'following' ? (requeue ? recheck : addDays(recheck, 365)) : null,
      });
      onClose();
    } catch (e) {
      setError(errorText(e));
    }
  };
  useSubmitKey(() => void send());

  return (
    <Modal
      width={560}
      title="标记已处理"
      subtitle={anomaly.title}
      onClose={onClose}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button variant="primary" disabled={resolve.isPending} onClick={() => void send()}>
            确认
          </Button>
          <Kbds keys={['⌘', '↵']} />
        </>
      }
    >
      <div style={{ margin: '-16px -20px 0' }}>
        <div className="t-group" style={{ padding: '12px 20px 4px' }}>
          处理结论
        </div>
        {options.map((o) => (
          <button key={o.kind} type="button" className={clsx('ib-opt', kind === o.kind && 'on')} onClick={() => setKind(o.kind)}>
            <span>
              <Radio checked={kind === o.kind} />
            </span>
            <span className="col" style={{ gap: 2 }}>
              <span className="c1" style={{ fontSize: 13 }}>
                {o.title}
              </span>
              <span className="t-meta">{o.sub}</span>
            </span>
          </button>
        ))}
        <div className="t-group" style={{ padding: '16px 20px 6px' }}>
          备注 <span style={{ fontWeight: 400 }}>· 可选，会记在项目动态里</span>
        </div>
        <textarea className="wd-textarea ib-text" rows={2} value={note} onChange={(e) => setNote(e.target.value)} aria-label="备注" />
        {kind === 'following' && (
          <div className="row" style={{ gap: 10, padding: '16px 20px', fontSize: 13, color: 'var(--text-2)' }}>
            <button type="button" onClick={() => setRequeue(!requeue)} aria-label="到期放回收件箱">
              <Checkbox checked={requeue} />
            </button>
            <input
              type="date"
              className="ib-select"
              value={recheck}
              min={addDays(todayCN(), 1)}
              onChange={(e) => e.target.value && setRecheck(e.target.value)}
              aria-label="复查日期"
            />
            <span>仍未解除时，重新放回收件箱</span>
          </div>
        )}
        <ErrorLine error={error} />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// 被指派人：无法完成 / 转给他人（设计稿 18b）
// ---------------------------------------------------------------------------

export function ReturnModal({ assignment, onClose }: { assignment: AssignmentDTO; onClose: () => void }) {
  const { giveBack } = useInboxActions();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    if (!reason.trim()) return;
    try {
      await giveBack.mutateAsync({ id: assignment.id, reason });
      onClose();
    } catch (e) {
      setError(errorText(e));
    }
  };
  useSubmitKey(() => void send());
  return (
    <Modal
      width={520}
      title="无法完成"
      subtitle={assignment.anomalyTitle}
      onClose={onClose}
      footNote={`会退回给${assignment.assignerName}，并说明原因`}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button variant="primary" disabled={!reason.trim()} onClick={() => void send()}>
            退回
          </Button>
        </>
      }
    >
      <textarea
        className="wd-textarea"
        rows={3}
        autoFocus
        value={reason}
        placeholder="为什么做不了？需要谁来做？"
        onChange={(e) => setReason(e.target.value)}
        aria-label="原因"
      />
      <ErrorLine error={error} />
    </Modal>
  );
}

export function TransferModal({ assignment, onClose }: { assignment: AssignmentDTO; onClose: () => void }) {
  const { transfer } = useInboxActions();
  const { data: members = [] } = useMembers();
  const [toId, setToId] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    if (!toId) return;
    try {
      await transfer.mutateAsync({ id: assignment.id, toId, note: note.trim() || null });
      onClose();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Modal
      width={520}
      title="转给他人"
      subtitle={assignment.anomalyTitle}
      onClose={onClose}
      footNote={`${assignment.assignerName}会收到通知`}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button variant="primary" disabled={!toId} onClick={() => void send()}>
            转交
          </Button>
        </>
      }
    >
      <div className="col" style={{ gap: 12 }}>
        <select className="ib-select" value={toId} onChange={(e) => setToId(e.target.value)} aria-label="转给谁">
          <option value="">选择同事…</option>
          {members
            .filter((m) => m.id !== assignment.assigneeId)
            .map((m) => (
              <option key={m.id} value={m.id}>
                {m.name} · {m.title ?? m.teamName ?? ''}
              </option>
            ))}
        </select>
        <textarea className="wd-textarea" rows={2} value={note} placeholder="补充一句（可选）" onChange={(e) => setNote(e.target.value)} aria-label="补充说明" />
      </div>
      <ErrorLine error={error} />
    </Modal>
  );
}

export function RejectExitModal({ request, onClose }: { request: ExitRequestDTO; onClose: () => void }) {
  const { rejectExit } = useInboxActions();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      width={520}
      title="暂不同意"
      subtitle={`${request.memberName}申请退出 ${request.projectCode}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="text" onClick={onClose}>
            取消
          </Button>
          <Button
            variant="primary"
            disabled={!reason.trim()}
            onClick={async () => {
              try {
                await rejectExit.mutateAsync({ id: request.id, reason });
                onClose();
              } catch (e) {
                setError(errorText(e));
              }
            }}
          >
            发送
          </Button>
        </>
      }
    >
      <textarea
        className="wd-textarea"
        rows={3}
        autoFocus
        value={reason}
        placeholder="写一句原因，例如：老化报告交接完再退出"
        onChange={(e) => setReason(e.target.value)}
        aria-label="原因"
      />
      <ErrorLine error={error} />
    </Modal>
  );
}
